/**
 * Polygon offset utilities — edge-based inset/outset for closed 2D polygons.
 *
 * Extracted from OutlineNode so RoofNode (bevel style) can reuse the same
 * offset algorithm.
 */

type Point2D = { x: number; y: number };

/**
 * Compute signed area to determine winding order.
 * Positive = CCW, Negative = CW
 */
export function signedArea(points: Point2D[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return area / 2;
}

/**
 * Find intersection point of two lines (extended to infinity).
 * Returns null if lines are parallel.
 */
export function lineIntersection(
  p1: Point2D, p2: Point2D,
  p3: Point2D, p4: Point2D
): Point2D | null {
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;

  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) {
    return null; // Parallel
  }

  const dx = p3.x - p1.x;
  const dy = p3.y - p1.y;
  const t = (dx * d2y - dy * d2x) / cross;

  return {
    x: p1.x + t * d1x,
    y: p1.y + t * d1y
  };
}

/**
 * Check if two line segments intersect (bounded).
 * Returns the intersection point if they do, null otherwise.
 */
export function segmentIntersection(
  p1: Point2D, p2: Point2D,
  p3: Point2D, p4: Point2D
): Point2D | null {
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;

  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) {
    return null; // Parallel
  }

  const dx = p3.x - p1.x;
  const dy = p3.y - p1.y;
  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;

  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return {
      x: p1.x + t * d1x,
      y: p1.y + t * d1y
    };
  }
  return null;
}

/**
 * Offset a closed polygon using edge-based intersection method.
 * Positive offset = outward (expand), Negative = inward (shrink).
 * Handles sharp corners by computing offset edges and finding intersections.
 */
export function offsetPoints(points: Point2D[], offset: number): Point2D[] {
  if (points.length < 3) return points;

  // Detect winding order and adjust offset direction
  const area = signedArea(points);
  const windingSign = area >= 0 ? -1 : 1;
  const adjustedOffset = offset * windingSign;

  // Step 1: Compute offset edges (each edge shifted by offset along its normal)
  const offsetEdges: Array<{
    p1: Point2D;
    p2: Point2D;
    nx: number;
    ny: number;
  }> = [];

  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    // Perpendicular normal (left side of edge direction)
    const nx = -dy / len;
    const ny = dx / len;

    offsetEdges.push({
      p1: { x: p1.x + nx * adjustedOffset, y: p1.y + ny * adjustedOffset },
      p2: { x: p2.x + nx * adjustedOffset, y: p2.y + ny * adjustedOffset },
      nx,
      ny
    });
  }

  // Step 2: Find intersections between consecutive offset edges
  const result: Point2D[] = [];

  for (let i = 0; i < offsetEdges.length; i++) {
    const edge1 = offsetEdges[i];
    const edge2 = offsetEdges[(i + 1) % offsetEdges.length];

    const intersection = lineIntersection(
      edge1.p1, edge1.p2,
      edge2.p1, edge2.p2
    );

    if (intersection) {
      const origVertex = points[(i + 1) % points.length];
      const dist = Math.sqrt(
        (intersection.x - origVertex.x) ** 2 +
        (intersection.y - origVertex.y) ** 2
      );

      // Limit intersection distance to prevent extreme spikes
      const maxDist = Math.abs(adjustedOffset) * 3;
      if (dist < maxDist) {
        result.push(intersection);
      } else {
        result.push({
          x: (edge1.p2.x + edge2.p1.x) / 2,
          y: (edge1.p2.y + edge2.p1.y) / 2
        });
      }
    } else {
      // Parallel edges - use midpoint
      result.push({
        x: (edge1.p2.x + edge2.p1.x) / 2,
        y: (edge1.p2.y + edge2.p1.y) / 2
      });
    }
  }

  return result;
}

/**
 * Remove self-intersections from a closed polygon.
 * When edges cross, keep the portion that maintains valid winding.
 */
export function removeSelfIntersections(points: Point2D[]): Point2D[] {
  if (points.length < 4) return points;

  let result = [...points];
  const maxIterations = points.length;

  for (let iter = 0; iter < maxIterations; iter++) {
    let foundIntersection = false;

    for (let i = 0; i < result.length && !foundIntersection; i++) {
      const p1 = result[i];
      const p2 = result[(i + 1) % result.length];

      for (let j = i + 2; j < result.length; j++) {
        if (j === (i - 1 + result.length) % result.length) continue;
        if ((j + 1) % result.length === i) continue;

        const p3 = result[j];
        const p4 = result[(j + 1) % result.length];

        const ix = segmentIntersection(p1, p2, p3, p4);
        if (ix) {
          const newResult: Point2D[] = [];
          for (let k = 0; k <= i; k++) {
            newResult.push(result[k]);
          }
          newResult.push(ix);
          for (let k = j + 1; k < result.length; k++) {
            newResult.push(result[k]);
          }
          result = newResult;
          foundIntersection = true;
          break;
        }
      }
    }

    if (!foundIntersection) break;
  }

  return result;
}
